import { pickT } from "@repo/shared";
import { Columns2, Maximize, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { themeVars } from "@/components/embed-frame";
import {
  Dialog,
  DialogBody,
  DialogScrollContent,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ComponentStyleType } from "@/lib/types/flow";
import { EmbedPreview } from "./embed-preview";
import { T } from "./embed-preview-dialog.i18n";

type EmbedPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  html: string;
  flowLanguage: string;
  emptyLabel: string;
  frameStyle?: ComponentStyleType;
  title?: string;
  description?: string;
};

/**
 * Full-size preview (QL-178). Out of the editor and into its own modal so the
 * authoring column stays short, and with the two comparisons that actually
 * catch problems: a widget squeezed into one side of a 2-column block, and the
 * palette the advisor is not currently using.
 */
export function EmbedPreviewDialog({
  open,
  onOpenChange,
  html,
  flowLanguage,
  emptyLabel,
  frameStyle,
  title,
  description,
}: EmbedPreviewDialogProps) {
  const t = pickT(T, flowLanguage);
  const [width, setWidth] = useState<"half" | "full">("full");
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    document.documentElement.classList.contains("dark") ? "dark" : "light",
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogScrollContent className="max-w-5xl">
        <DialogScrollHeader>
          <div className="flex items-center justify-between gap-4 pr-8">
            <div>
              <DialogTitle>{t.title}</DialogTitle>
              <p className="text-sm text-muted-foreground">{t.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Tabs value={width} onValueChange={(v) => setWidth(v as "half" | "full")}>
                <TabsList>
                  <TabsTrigger value="half">
                    <Columns2 className="mr-2 h-4 w-4" />
                    {t.halfWidth}
                  </TabsTrigger>
                  <TabsTrigger value="full">
                    <Maximize className="mr-2 h-4 w-4" />
                    {t.fullWidth}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Tabs value={theme} onValueChange={(v) => setTheme(v as "light" | "dark")}>
                <TabsList>
                  <TabsTrigger value="light">
                    <Sun className="mr-2 h-4 w-4" />
                    {t.light}
                  </TabsTrigger>
                  <TabsTrigger value="dark">
                    <Moon className="mr-2 h-4 w-4" />
                    {t.dark}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </DialogScrollHeader>

        <DialogBody>
          {/* The canvas stands in for a flow page, so the whole of it — not just
              the widget — renders in the chosen theme. The palette is written as
              inline custom properties rather than a `dark` class: the class
              variant only adds, so a subtree cannot return to light while the
              app's `html.dark` is setting the variables. */}
          <div
            className={`rounded-lg bg-background p-6 ${theme === "dark" ? "dark" : ""}`}
            style={themeVars(theme)}
          >
            <div className={width === "half" ? "max-w-[50%]" : undefined}>
              <EmbedPreview
                html={html}
                emptyLabel={emptyLabel}
                frameStyle={frameStyle}
                title={title}
                description={description}
                theme={theme}
              />
            </div>
          </div>
        </DialogBody>
      </DialogScrollContent>
    </Dialog>
  );
}
